import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import { Box } from "@mui/material";
import { red } from '@mui/material/colors';
import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import productionExceptionsService from "@plannel/services/mp/production-exceptions-service";

import SplitterContainer from "@plannel/components/SplitterContainer";
import ChartComposed from "@plannel/components/rechart/ChartComposed";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import Snackbar from "@plannel/components/Snackbar";

import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import { MPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import MpVersionFilter from "@plannel/components/filter/MpVersionFilter";
import { MPLocationTreeFilter } from "@plannel/components/filter/LocationTreeFilter";

const { snackBarMessageColor } = ColorUtil;
const ProductionExceptions = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  const { isPermitted } = useMenuAccess();

  const pageMoveFilterRef = useRef({});
  const [mpVersionFilter, setMpVersionFilter] = useState(reduxViewState?.mpVersion ? reduxViewState.mpVersion : null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null, category: null });

  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);

  const { versionStatusCode } = CodeUtil;
  const { RELEASED, PLANNED } = versionStatusCode;

  const [gridLoading, setGridLoading] = useState(false);
  const [prodExceptions, setProdExceptions] = useState([]);
  const [totalRowData, setTotalRowData] = useState({});

  const gridRef = useRef();
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const defaultGridMemo = useMemo(() => {
    const defaultSettings = DefaultGridSetting({ title, viewName });
    defaultSettings.defaultColDef.filter = true;
    return defaultSettings;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 상세 화면이 권한 밖이면 링크 표시(파란 밑줄)도 클릭도 없앤다.
  const canOpenDetail = isPermitted("MP_PRODUCTION_EXCEPTIONS_DETAIL");

  const remainingCellStyle = params => {
    const { value, data } = params;
    if (canOpenDetail && value > 0 && data?.baseDate) {
      return { color: 'blue', textDecoration: 'underline', cursor: 'pointer' };
    }

    return {};
  };

  const onRemainingClicked = (params) => {
    const { data, value } = params;

    if (canOpenDetail && value > 0 && data?.baseDate) {
      const reduxKey = "MP_PRODUCTION_EXCEPTIONS_DETAIL";
      const reduxState = reduxUtil.createObjectState(reduxKey, {
        itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current,
        baseDate: data.baseDate,
        ...pageMoveFilterRef.current
      });

      reduxDispatch(reduxUtil.updateViewState(reduxState));
      reduxDispatch(reduxUtil.updateHistoryState(reduxKey));
    }
  };

  const columnDefs = [
    { headerName: "", field: "displayHeader", type: ["defaultValueFormatter", "nonEditableColumn"], flex: 1, pinned: "left", lockPinned: true },
    { headerName: "totalExceptions", field: "totalExceptions", type: ["numberColumn", "nonEditableColumn", "rightAligned"], flex: 2 },
    { headerName: "exceptionsComplete", field: "exceptionsComplete", type: ["numberColumn", "nonEditableColumn", "rightAligned"], flex: 2 },
    {
      headerName: "exceptionsRemaining", field: "exceptionsRemaining", type: ["numberColumn", "nonEditableColumn", "rightAligned"], flex: 2,
      cellStyle: remainingCellStyle, onCellClicked: onRemainingClicked
    },
    { headerName: "complete", field: "completeRatio", type: ["percentColumn", "nonEditableColumn", "rightAligned"], flex: 2 }
  ];

  const defaultTypeOptions = { stackId: "exception", strokeWidth: 1.5, barSize: 40 };
  const chartSeries = [
    {
      id: "exceptionsRemaining",
      field: "exceptionsRemaining",
      text: t("exceptionsRemaining"),
      type: "bar",
      typeOptions: { ...defaultTypeOptions, fill: red[300] },
      groupName: t("total")
    },
    {
      id: "exceptionsComplete",
      field: "exceptionsComplete",
      text: t("exceptionsComplete"),
      type: "bar",
      typeOptions: { ...defaultTypeOptions, fill: ColorUtil.colorSet(0) },
      groupName: t("total")
    }
  ];

  const getRequestParams = () => {
    const params = {};

    if (filterValue?.locations) {
      params.locCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (!isEmpty(mpVersionFilter)) {
        params.versionId = mpVersionFilter.id;
    }

    if (filterValue.category) {
        params.itemHrchyCd = filterValue.category.hrchyCd;
    }

    if (extraItemCodesRef.current?.length > 0) {
      params.itemCds = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
        params.itemCd = itemSearch.current;
    }

    return params;
  };

  const getTotalRow = results => {
    const totalRow = { totalExceptions: 0, exceptionsComplete: 0, exceptionsRemaining: 0, completeRatio: 0 };
    for (const result of results) {
      totalRow.totalExceptions += result.totalExceptions;
      totalRow.exceptionsComplete += result.exceptionsComplete;
    }
    totalRow.exceptionsRemaining = totalRow.totalExceptions - totalRow.exceptionsComplete;
    totalRow.completeRatio = totalRow.totalExceptions > 0 ? totalRow.exceptionsComplete / totalRow.totalExceptions : 0;

    setTotalRowData(totalRow);
  };

  const retrieveProdExceptions = () => {
    gridRef.current?.api?.setGridOption('loading', true);

    const params = getRequestParams();
    productionExceptionsService.getAll(params)
    .then(response => {
      const { results } = response.data;
      if (isEmpty(results)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
      }
      setProdExceptions(results);
      getTotalRow(results);
    })
    .catch(e => {
      debug(e);
    })
    .then(() => {
      const reduxState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current });
      const filterState = reduxUtil.createFilterViewState(viewName, filterValue);
      reduxDispatch(reduxUtil.updateViewState({ ...reduxState, ...filterState }));

      gridRef.current?.api?.setGridOption('loading', false);
    })
  };

  const onGridReady = params => {
    setGridLoading(true);

    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  };

  useEffect(() => {
    if (gridLoading && !isEmpty(mpVersionFilter)) {
      pageMoveFilterRef.current = { mpVersion: mpVersionFilter, filter: filterValue };
      retrieveProdExceptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpVersionFilter?.id, gridLoading, filterValue]);

  return (
  <>
    <Box id={"grid-with-chart"} sx={{ height: "calc(100vh - 135px)" }}>
      <FilterContainer hasIcons={false}>
        <MpVersionFilter viewName={viewName} mpVersionValue={mpVersionFilter} onChangeFilter={setMpVersionFilter} statusFilters={[PLANNED, RELEASED]} />
        <MPLocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} />
        <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} label={"itemHrchy"} styles={{ width: 250 }}/>
        <MPItemAutocomplete retrieve={retrieveProdExceptions} searchValue={itemSearch} key={"ITEM"} sx={{ width: 250 }} extraCodesRef={extraItemCodesRef} />
      </FilterContainer>      <SplitterContainer initialSizes={[1, 1]} collapseDirection='up'>
        <Box className="chart-area" sx={{ height: "100%" }}>
          <ChartComposed
          xAxis={"displayHeader"}
          dataSeries={chartSeries}
          data={prodExceptions}
          options={{ margin: { left: 10, top: 20, right: 20, bottom: 30 } }}
          tooltipAnimation={false}
          />
        </Box>
        <Box id="inventory-exceptions-grid" className="ag-theme-balham" sx={{ height: "calc(100% - 35px)", mt: 2}}>
          <AgGridReact
          ref={gridRef}
          rowData={prodExceptions}
          {...defaultGridMemo}
          pinnedBottomRowData={[totalRowData]}
          onGridReady={onGridReady}
          />
        </Box>
      </SplitterContainer>
    </Box>
    <Snackbar
    open={snackInfo.open}
    onClose={() => setSnackInfo({ ...snackInfo, open: false })}
    severity={snackInfo.severity}
    content={snackInfo.content}
    />
  </>
  );
};

export default withTranslation()(ProductionExceptions);
